import { memo as Memo, useMemo, useEffect, useRef, Fragment } from 'react';
import { TweenMax } from 'gsap';

//* HOC's
import withLanguageContext from '@/context/consumerHOC/LanguageConsumer';

//* Helpers
import breakString from '@helpers/breakString';

//* Components
import Text from '@/components/global/Text';
import Button from '@components/global/Button';
import CustomLottie from '@/components/global/CustomLottie';
import ArrowRight from '@components/global/SvgComponents/ArrowRight';

//* JSONs
import animJSON from './animJSON.json';

//* Styles
import PageTopSectionStyle from './style';

const PageTopSection = Memo(({ time, pageTop, type, translate, className, ...props }) => {
	//! Refs
	const topRef1 = useRef();
	const topRef2 = useRef();
	const topRef3 = useRef();
	const topRef4 = useRef();
	const timeout1 = useRef();
	const timeout2 = useRef();

	useEffect(() => {
		timeout1.current = setTimeout(() => {
			TweenMax.staggerTo(
				[topRef1?.current?.children, topRef2?.current?.children, topRef3?.current, topRef4?.current],
				0,
				{
					opacity: 1,
				},
				0.3
			);
			timeout2.current = setTimeout(() => {
				TweenMax.staggerTo([topRef1?.current?.children[0]], 0, {
					css: { fontStyle: 'italic', fontWeight: 300 },
				});
			}, 200);
		}, time || 0);

		return () => {
			timeout1.current && clearTimeout(timeout1.current);
			timeout2.current && clearTimeout(timeout2.current);
		};
	}, []);

	const otherInfo = useMemo(() => {
		let functional_other_info = pageTop.description;
		const matches = functional_other_info?.match(/\[(.*?)\]/);
		functional_other_info = matches ? functional_other_info.split(matches[0]) : functional_other_info;

		functional_other_info = props.onOtherInfoClick
			? functional_other_info.map((str, k) => {
					return (
						<Fragment key={k}>
							{str}
							{k === 0 && (
								<span
									className='font-underline'
									onClick={props.onOtherInfoClick}>
									{matches[1]}
								</span>
							)}
						</Fragment>
					);
			  })
			: functional_other_info;

		return (
			functional_other_info && (
				<Text
					tag={'p'}
					ref={topRef4}
					className={`p-l font-poppins font-light otherInfo`}>
					{functional_other_info}
				</Text>
			)
		);
	}, [pageTop]);

	return (
		<PageTopSectionStyle className={`page-top-section-container-wrap ${className || ''}`}>
			<div className={'titleWrapCont'}>
				<div className={'titleWrap'}>
					{pageTop.title && (
						<Text
							tag={'h1'}
							ref={topRef1}
							className={`h1 font-poppins font-semi-bold title`}>
							{breakString(pageTop.title)}
						</Text>
					)}

					{type !== 2 && (
						<div
							ref={topRef2}
							className={'topBtnAnimationWrap'}>
							<div className={'topBtnArrow'}>
								<ArrowRight animate />
							</div>

							<Button
								turnAnimationParams
								text={translate('clickHere')}
								otherLink={'/contact-form/'}
							/>
						</div>
					)}
				</div>

				{type === 2 && (
					<div
						ref={topRef2}
						className={'topAnimationWrap'}>
						<CustomLottie animData={animJSON} />
					</div>
				)}
			</div>

			{pageTop.subtitle ? (
				<Text
					tag={'h3'}
					ref={topRef3}
					className={`${pageTop.settings?.text_size_small ? `h6 TextSizeSmall` : `h5 description`} font-poppins font-light`}>
					{pageTop.subtitle}
				</Text>
			) : null}

			{otherInfo}
		</PageTopSectionStyle>
	);
});

export default withLanguageContext(PageTopSection, ['translate']);
